/**
 * useHoldedInvoices - Hook para gestionar la integración con Holded
 *
 * Proporciona funciones para:
 * 1. Obtener facturas pendientes de generar en Holded
 * 2. Generar facturas en Holded (crear documentos y PDFs)
 * 3. Confirmar facturas en Holded (finalizar y procesar pagos)
 */

import { createHttpClient } from '@adapters/HttpClient';
import { useToast } from '@shared/hooks/use-toast';
import { apiClientWH } from '@shared/services/ApiClient';
import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { InvoicesApiAdapter } from '../adapters/InvoicesApiAdapter';
import { HoldedInvoicesModalRef } from '../components/modals';

export const useHoldedInvoices = () => {
  const { t } = useTranslation('invoicesModule');
  const { toast } = useToast();
  const [isGenerating, setIsGenerating] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [isLoadingPending, setIsLoadingPending] = useState(false);
  const [isLoadingPendingConfirm, setIsLoadingPendingConfirm] = useState(false);
  const modalRef = useRef<HoldedInvoicesModalRef>(null);

  /**
   * Genera facturas en Holded (Paso 1)
   * Busca facturas sin PDF y las crea en Holded
   */
  const generateInvoices = useCallback(
    async (onSuccess?: () => void) => {
      setIsGenerating(true);

      try {
        const adapter = new InvoicesApiAdapter(createHttpClient(apiClientWH));
        const result = await adapter.generateHoldedInvoices();

        const count = result.data?.length || 0;

        toast({
          title: t('toast.holded.generate.success.title', 'Facturas generadas'),
          description: t('toast.holded.generate.success.description', {
            count,
            defaultValue: `Se generaron ${count} facturas en Holded`,
          }),
          variant: 'default',
        });

        onSuccess?.();
      } catch (error: any) {
        const status = error?.response?.status;

        if (status === 404) {
          toast({
            title: t('toast.holded.generate.not_found.title', 'Sin facturas pendientes'),
            description: t(
              'toast.holded.generate.not_found.description',
              'No hay facturas pendientes de generar en Holded'
            ),
            variant: 'default',
          });
        } else {
          toast({
            title: t('toast.holded.generate.error.title', 'Error al generar'),
            description:
              error?.message ||
              t('toast.holded.generate.error.description', 'Error al generar facturas en Holded'),
            variant: 'destructive',
          });
        }
      } finally {
        setIsGenerating(false);
      }
    },
    [t, toast]
  );

  /**
   * Obtiene facturas pendientes de generar en Holded y muestra el modal
   */
  const showPendingInvoices = useCallback(
    async (onSuccess?: () => void) => {
      setIsLoadingPending(true);

      try {
        const adapter = new InvoicesApiAdapter(createHttpClient(apiClientWH));
        const result = await adapter.getPendingHoldedInvoices();

        const pendingInvoices = result.data || [];

        if (pendingInvoices.length === 0) {
          toast({
            title: t('toast.holded.generate.not_found.title', 'Sin facturas pendientes'),
            description: t(
              'toast.holded.generate.not_found.description',
              'No hay facturas pendientes de generar en Holded'
            ),
            variant: 'default',
          });
          return;
        }

        // Mostrar modal con las facturas pendientes
        modalRef.current?.open(pendingInvoices, () => {
          generateInvoices(onSuccess);
        }, 'generate');
      } catch (error: any) {
        toast({
          title: t('toast.holded.generate.error.title', 'Error al obtener facturas'),
          description:
            error?.message ||
            t('toast.holded.generate.error.description', 'Error al obtener facturas pendientes'),
          variant: 'destructive',
        });
      } finally {
        setIsLoadingPending(false);
      }
    },
    [t, toast, generateInvoices]
  );

  /**
   * Confirma facturas en Holded (Paso 2)
   * Finaliza facturas y procesa los pagos
   */
  const confirmInvoices = useCallback(
    async (onSuccess?: () => void) => {
      setIsConfirming(true);

      try {
        const adapter = new InvoicesApiAdapter(createHttpClient(apiClientWH));
        await adapter.confirmHoldedInvoices();

        toast({
          title: t('toast.holded.confirm.success.title', 'Facturas confirmadas'),
          description: t(
            'toast.holded.confirm.success.description',
            'Las facturas han sido confirmadas en Holded y los pagos procesados'
          ),
          variant: 'default',
        });

        onSuccess?.();
      } catch (error: any) {
        const status = error?.response?.status;

        if (status === 404) {
          toast({
            title: t('toast.holded.confirm.not_found.title', 'Sin facturas pendientes'),
            description: t(
              'toast.holded.confirm.not_found.description',
              'No hay facturas pendientes de confirmar en Holded'
            ),
            variant: 'default',
          });
        } else {
          toast({
            title: t('toast.holded.confirm.error.title', 'Error al confirmar'),
            description:
              error?.message ||
              t('toast.holded.confirm.error.description', 'Error al confirmar facturas en Holded'),
            variant: 'destructive',
          });
        }
      } finally {
        setIsConfirming(false);
      }
    },
    [t, toast]
  );

  /**
   * Obtiene facturas pendientes de confirmar en Holded y muestra el modal
   */
  const showPendingConfirmInvoices = useCallback(
    async (onSuccess?: () => void) => {
      setIsLoadingPendingConfirm(true);

      try {
        const adapter = new InvoicesApiAdapter(createHttpClient(apiClientWH));
        const result = await adapter.getPendingConfirmHoldedInvoices();

        const pendingInvoices = result.data || [];

        if (pendingInvoices.length === 0) {
          toast({
            title: t('toast.holded.confirm.not_found.title', 'Sin facturas pendientes'),
            description: t(
              'toast.holded.confirm.not_found.description',
              'No hay facturas pendientes de confirmar en Holded'
            ),
            variant: 'default',
          });
          return;
        }

        // Mostrar modal con las facturas pendientes
        modalRef.current?.open(pendingInvoices, () => {
          confirmInvoices(onSuccess);
        }, 'confirm');
      } catch (error: any) {
        toast({
          title: t('toast.holded.confirm.error.title', 'Error al obtener facturas'),
          description:
            error?.message ||
            t('toast.holded.confirm.error.description', 'Error al obtener facturas pendientes de confirmar'),
          variant: 'destructive',
        });
      } finally {
        setIsLoadingPendingConfirm(false);
      }
    },
    [t, toast, confirmInvoices]
  );

  return {
    showPendingInvoices,
    showPendingConfirmInvoices,
    generateInvoices,
    confirmInvoices,
    isGenerating,
    isConfirming,
    isLoadingPending,
    isLoadingPendingConfirm,
    isProcessing: isGenerating || isConfirming || isLoadingPending || isLoadingPendingConfirm,
    modalRef,
  };
};

